import React, {useState, useMemo} from "react";
import {observer} from "mobx-react-lite";
import {useTranslation} from "react-i18next";
import {message, InputNumber, Alert} from "antd";
import {useStore} from "hooks/useStore";
import {numericInputProps} from "utils/helpers";
import RequestsDataTable from "./RequestsDataTable";
import RequestDetailModal from "./RequestDetailModal";
import CourierDecreaseConfirmationModal from "./CourierDecreaseConfirmationModal";
import {
    SelectionCard,
    SelectionTitle,
    CourierCountContainer,
    DecreaseRequestWarning,
    DecreaseButtonContainer,
    ConfirmButton,
    CancelButton
} from "../style/CustomerRequestsPage.styles";

const CourierDecreaseRequest = ({
    tableData,
    tableLoading,
    refetchTable,
    onPaginationChange,
    filters,
    handleUpdateFilters
}) => {
    const {t} = useTranslation();
    const stores = useStore();
    const [courierCount, setCourierCount] = useState(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);

    const requestsData = tableData?.data || [];

    const handleConfirm = () => {
        if (!courierCount || courierCount < 1) {
            message.warning(t("screens.customerRequests.pleaseEnterValidCourierCount"));
            return;
        }

        setConfirmationModalVisible(true);
    };

    const handleConfirmationModalConfirm = async () => {
        setLoading(true);

        const params = {
            type: "planning",
            subType: "decrement",
            courierCount: courierCount,
            subCompanyId: stores?.meStore?.selectedSubCompanyId || "all"
        };

        const result = await stores.shiftRequestStore.createShiftRequest(params);
        if (result) {
            setCourierCount(null);
            setConfirmationModalVisible(false);
            refetchTable();
        }

        setLoading(false);
    };

    const handleConfirmationModalCancel = () => {
        setConfirmationModalVisible(false);
    };

    const handleCancel = () => {
        message.info(t("screens.customerRequests.operationCancelled"));
        setCourierCount(null);
    };



    const handleCloseDetailModal = () => {
        setDetailModalVisible(false);
        setSelectedRequest(null);
    };

    return (
        <>
            <Alert
                type="warning"
                showIcon
                message={t("screens.customerRequests.onlyOneRequestPerDay")}
                style={{marginBottom: "16px"}}
            />
            <SelectionCard style={{marginTop: "20px"}}>
                <SelectionTitle>
                    {t("screens.customerRequests.decreaseRequestTitle")}
                </SelectionTitle>
                <CourierCountContainer style={{marginTop: "16px", marginBottom: "24px"}}>
                    <label style={{fontWeight: 500, fontSize: "14px"}}>
                        {t("screens.customerRequests.decreaseRequestLabel")}
                    </label>
                    <InputNumber
                        min={1}
                        max={100}
                        value={courierCount}
                        onChange={setCourierCount}
                        placeholder={t("screens.customerRequests.enterCount")}
                        style={{width: "80px", marginLeft: "12px"}}
                        {...numericInputProps}
                    />
                </CourierCountContainer>

                <DecreaseRequestWarning>
                    {t("screens.customerRequests.decreaseRequestWarning")} <br />
                    {t("screens.customerRequests.decreaseRequestConfirmation")}
                </DecreaseRequestWarning>

                <DecreaseButtonContainer>
                    <CancelButton onClick={handleCancel} disabled={loading}>
                        {t("screens.customerRequests.no")}
                    </CancelButton>
                    <ConfirmButton onClick={handleConfirm} disabled={loading}>
                        {loading
                            ? t("screens.customerRequests.loading")
                            : t("screens.customerRequests.yes")}
                    </ConfirmButton>
                </DecreaseButtonContainer>
            </SelectionCard>
            <RequestDetailModal
                visible={detailModalVisible}
                onClose={handleCloseDetailModal}
                requestData={selectedRequest}
                refetchTable={refetchTable}
            />
            <CourierDecreaseConfirmationModal
                visible={confirmationModalVisible}
                onCancel={handleConfirmationModalCancel}
                onConfirm={handleConfirmationModalConfirm}
                courierCount={courierCount}
                loading={loading}
            />
        </>
    );
};

export default observer(CourierDecreaseRequest);
